import { Component, Input, OnInit } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ServiceTemplates } from './serviceTemplates';
import { IntegrationsService } from '../../_services';

@Component({
  standalone: false,
  selector: 'integration-generic',
  templateUrl: './integration-generic.component.html',
  styleUrls: ['./integration-generic.component.css'],
})

export class IntegrationGenericComponent implements OnInit {
  @Input() settingsForm: FormGroup;
  @Input() settings: any;

  isTesting = false;
  testResult: any = null;
  serviceTemplates = ServiceTemplates;

  applyTemplate(templateKey: string | null) {
    if (!templateKey) {
      return;
    }

    const template = this.serviceTemplates.find(x => x.key === templateKey);

    if (!template) {
      return;
    }

    this.settings.url = template.url;
    this.settings.httpMethod = template.method;

    this.settings.headers = template.headers.map(x => ({ ...x }));
    this.settings.parameters = template.parameters.map(x => ({ ...x }));
    this.restoreForm();
  }

  ngOnInit(): void {
    if (typeof this.settings === 'string') {
      try {
        this.settings = JSON.parse(this.settings);
      }
      catch (error) {
        console.error('Failed to deserialize settings', error);
      }
    }
    this.settings = this.settings ?? {};

    this.settings.headers = [];
    this.settings.parameters = [];

    for (const key of Object.keys(this.settings)) {
      if (key.startsWith('header_key')) {
        const id = key.replace('header_key_', '');
        this.settings.headers.push({
          id: id,
          key: this.settings[key],
          value: this.settings[key.replace('header_key_', 'header_value_')],
          enabled: true
        })
      }

      if (key.startsWith('param_key')) {
        const id = key.replace('param_key_', '');
        this.settings.parameters.push({
          id: id,
          key: this.settings[key],
          value: this.settings[key.replace('param_key_', 'param_value_')],
          enabled: true
        })
      }
    }

    this.restoreForm();
  }

  constructor(private http: HttpClient,
    private fb: FormBuilder,
    private integrationsService: IntegrationsService) { }

  restoreForm() {
    this.settings.url = this.settings.url ?? "";
    this.settings.httpMethod = this.settings.httpMethod ?? 'GET';
    this.settings.template = this.settings.template ?? "";

    this.clearForm();

    this.settingsForm.addControl(
      "url",
      this.fb.control(this.settings.url)
    );
    this.settingsForm.addControl(
      "httpMethod",
      this.fb.control(this.settings.httpMethod)
    );
    this.settingsForm.addControl(
      "template",
      this.fb.control(this.settings.template)
    );

    this.updateFormControls();
  }

  clearForm() {
    for (const controlName of Object.keys(this.settingsForm.controls)) {
      if (controlName.startsWith('header_')
        || controlName.startsWith('param_')
        || controlName === 'url'
        || controlName === 'httpMethod') {
        this.settingsForm.removeControl(controlName);
      }
    }
  }

  addHeader() {
    let header = { id: crypto.randomUUID(), key: '', value: '', enabled: true }
    this.settings.headers.push(header);
    this.updateFormControls();
  }

  removeHeader(index: number) {
    let param = this.settings.headers[index];
    this.settings.headers.splice(index, 1);
    this.settingsForm.removeControl("header_key_" + param.id);
    this.settingsForm.removeControl("header_value_" + param.id);
    this.settingsForm.removeControl("header_secret_" + param.id);
  }

  toggleHeaderSecret(id: string) {

    const control =
      this.settingsForm.get(`header_secret_${id}`);

    control?.setValue(!control.value);

  }

  addParameter() {
    let parameter = { id: crypto.randomUUID(), key: '', value: '', enabled: true }
    this.settings.parameters.push(parameter);
    this.updateFormControls();
  }

  removeParameter(index: number) {
    let param = this.settings.parameters[index];
    this.settings.parameters.splice(index, 1);
    this.settingsForm.removeControl("param_key_" + param.id);
    this.settingsForm.removeControl("param_value_" + param.id);
    this.settingsForm.removeControl("param_secret_" + param.id);
  }

  toggleParamSecret(id: string) {

    const control =
      this.settingsForm.get(`param_secret_${id}`);

    control?.setValue(!control.value);
  }

  updateFormControls() {
    for (let header of this.settings.headers) {
      header.id = header.id ?? crypto.randomUUID();
      if (!this.settingsForm.contains('header_key_' + header.id)) {
        this.settingsForm.addControl(
          'header_key_' + header.id,
          this.fb.control(
            header.key ?? '',
            Validators.required
          )
        );
        this.settingsForm.addControl(
          'header_value_' + header.id,
          this.fb.control(
            header.value ?? '',
            Validators.required
          )
        );
        this.settingsForm.addControl(
          'header_secret_' + header.id,
          this.fb.control(
            header.secret ?? ''
          )
        );
      }
    }

    for (let param of this.settings.parameters) {
      param.id = param.id ?? crypto.randomUUID();
      if (!this.settingsForm.contains('param_key_' + param.id)) {
        this.settingsForm.addControl(
          'param_key_' + param.id,
          this.fb.control(
            param.key ?? '',
            Validators.required
          )
        );
        this.settingsForm.addControl(
          'param_value_' + param.id,
          this.fb.control(
            param.value ?? '',
            Validators.required
          )
        );
        this.settingsForm.addControl(
          'param_secret_' + param.id,
          this.fb.control(
            param.secret ?? ''
          )
        );
      }
    }
  }

  testRequest() {
    for (let header of this.settings.headers) {
      header.key = this.settingsForm.get('header_key_' + header.id).value;
      header.value = this.settingsForm.get('header_value_' + header.id).value;
    }

    for (let param of this.settings.parameters) {
      param.key = this.settingsForm.get('param_key_' + param.id).value;
      param.value = this.settingsForm.get('param_value_' + param.id).value;
    }

    let request = {
      url: this.settingsForm.get('url').value,
      httpMethod: this.settingsForm.get('httpMethod').value,
      headers: this.settings.headers,
      parameters: this.settings.parameters
    };
    this.testResult = null;
    this.isTesting = true;
    this.integrationsService.tryExecute(request).subscribe(result => {
      this.testResult = result;
      this.isTesting = false;
     });
  }
}

